import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight, MessageSquare, CalendarCheck, ShieldCheck, MapPin, Train, LayoutGrid, Sun,
  Sparkles, Trees, Briefcase, GraduationCap, HeartPulse, Wallet, BadgeCheck, X,
} from "lucide-react";
import { initLenis } from "../lib/smooth";
import { getSiteSettings, digitsOnly } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/ContactFooter";
import { LeadModal } from "../components/LeadForm";
import { StickyMobileBar, WhatsAppFloat } from "../components/StickyElements";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal, SectionHeading } from "../components/Reveal";
import { IMAGES, AGENT_REG, FLOOR_PLANS, WA_MESSAGE } from "../data/site";

const TWO_BHK_PLANS = FLOOR_PLANS.filter((p) => p.category === "2 BHK");

const WHY_2BHK = [
  { icon: MapPin, title: "Location", desc: "A neighbourhood that keeps daily destinations close saves hours every week." },
  { icon: Train, title: "Connectivity", desc: "Metro access, arterial roads and realistic commute times matter more than straight-line distance." },
  { icon: LayoutGrid, title: "Efficient Space Planning", desc: "A well-planned 2 BHK with minimal passage can live larger than a poorly planned bigger flat." },
  { icon: Sun, title: "Natural Light & Ventilation", desc: "Open-sided homes with cross ventilation stay brighter and cooler." },
  { icon: Sparkles, title: "Amenities", desc: "Choose facilities your household will use weekly, not just a long brochure list." },
  { icon: Trees, title: "Green & Open Spaces", desc: "Parks and gardens inside the project shape daily life, especially for children." },
  { icon: Briefcase, title: "Employment Hubs", desc: "Proximity to IT and business districts like Action Area III shortens commutes." },
  { icon: GraduationCap, title: "Schools", desc: "Reputed schools within a short drive simplify family routines." },
  { icon: HeartPulse, title: "Healthcare", desc: "Hospitals and nursing homes nearby provide everyday reassurance." },
  { icon: Wallet, title: "Budget", desc: "Compare total cost including applicable charges, not just the base price." },
  { icon: BadgeCheck, title: "RERA Verification", desc: "Verify the project's and the agent's registration on the WBRERA website before booking." },
];

const TWO_BHK_FAQS = [
  {
    q: "Are 2 BHK flats available in New Town Kolkata?",
    a: "Yes. New Town offers many 2 BHK residential options, including near the Action Area III vicinity — for example, Vinayak 21 Acres at Hatishala, adjacent to Newtown Action Area III, offers 2 BHK 2T residences across four towers.",
  },
  {
    q: "What should I check before buying a 2 BHK in New Town?",
    a: "Compare carpet area rather than just super built-up area, study the floor plan and balcony provision, check the tower and floor, assess ventilation and open views, review amenities, ask for complete pricing with applicable charges, confirm current inventory, and verify RERA registration.",
  },
  {
    q: "Are 2 BHK homes available near Action Area III?",
    a: "Yes. Vinayak 21 Acres at Hatishala — off and adjacent to Newtown Action Area III — offers 2 BHK 2T homes of approximately 595–597 sq ft carpet area, depending on tower and type.",
  },
  {
    q: "Does Vinayak 21 Acres have 2 BHK apartments?",
    a: "Yes. The project offers 2 BHK 2T residences as Type B and Type G across Towers 1A, 1B, 2A and 2B — eight layout variants in total, each with an official floor plan you can view on this page.",
  },
  {
    q: "What is the carpet area of a 2 BHK at Vinayak 21 Acres?",
    a: "The 2 BHK 2T offers approximately 595–597 sq ft carpet area depending on tower and type, with about 60 sq ft balcony area, 728 sq ft BUA and 971 sq ft SBUA.",
  },
  {
    q: "What is the current price of a 2 BHK at Vinayak 21 Acres?",
    a: "Pricing and availability are subject to change. Contact Forest Hill Realtors for the latest developer-provided price and available inventory.",
  },
  {
    q: "How can I get the latest floor plan?",
    a: "You can view the official 2 BHK floor plans directly on this page or on our floor plans page. For a specific unit's detailed sheet, contact Forest Hill Realtors through the enquiry buttons or WhatsApp.",
  },
  {
    q: "How can I book a site visit?",
    a: "Use any Book Site Visit button on this page, the sticky enquiry bar on mobile, or message us on WhatsApp — Forest Hill Realtors will arrange a convenient slot.",
  },
];

export default function TwoBhk() {
  const [modal, setModal] = useState({ open: false, source: "2 BHK Page Enquiry", interest: "2 BHK" });
  const [planView, setPlanView] = useState(null);
  const [contact, setContact] = useState({ phone: "+919163841785", whatsapp: "+919163841785" });

  useEffect(() => {
    document.title = "2 BHK Flats in New Town Kolkata | New Town 2 BHK Property";
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        "Looking for 2 BHK flats in New Town Kolkata? Explore what buyers should consider and discover 2 BHK options at Vinayak 21 Acres near Newtown Action Area III."
      );
    initLenis();
    getSiteSettings().then(setContact);
  }, []);

  const openModal = (source, interest = "2 BHK") => setModal({ open: true, source, interest });
  const waLink = `https://wa.me/${digitsOnly(contact.whatsapp)}?text=${encodeURIComponent(WA_MESSAGE)}`;

  return (
    <div className="bg-[#FFFFFF]" data-testid="twobhk-page">
      <Header onBookVisit={() => openModal("Site Visit Booking")} phone={contact.phone} />
      <main>
        <section className="relative bg-[#EAF5D6] pt-36 pb-16 sm:pt-44 sm:pb-20 overflow-hidden hero-grain">
          <div className="absolute inset-0 opacity-[0.14]" aria-hidden="true">
            <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" loading="eager" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="overline-tag" data-testid="twobhk-overline">Buyer's Guide · Forest Hill Realtors</p>
            <h1
              className="font-serif-display text-[#3F6212] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 max-w-4xl"
              data-testid="twobhk-h1"
            >
              2 BHK Flats in <span className="text-[#84CC16]">New Town Kolkata</span>
            </h1>
            <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg max-w-2xl leading-relaxed">
              A practical guide for buyers searching for a 2 BHK flat in New Town — what to compare, what to verify,
              and a look at verified 2 BHK options at Vinayak 21 Acres near Newtown Action Area 3.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/" data-testid="twobhk-home-link"
                className="inline-flex items-center gap-2 text-[#84CC16] font-sub font-semibold text-sm border border-[#84CC16]/50 rounded-full px-6 py-3 hover:bg-[#84CC16]/10 active:scale-[0.98] transition-all duration-300">
                Explore Vinayak 21 Acres <ArrowRight size={15} />
              </Link>
              <Link to="/properties-in-new-town-action-area-3" data-testid="twobhk-aa3-link"
                className="inline-flex items-center gap-2 text-[#3F6212] font-sub font-semibold text-sm border border-[#3F6212]/30 rounded-full px-6 py-3 hover:bg-[#3F6212]/10 active:scale-[0.98] transition-all duration-300">
                Properties in New Town Action Area 3 <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </section>

        <section data-testid="twobhk-intro-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="01" eyebrow="Start Here" title="2 BHK Flats in New Town Kolkata" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  A 2 BHK remains the most searched configuration for 2 BHK residential property in New Town — large
                  enough for a growing family, efficient enough to stay within a sensible budget. But two 2 BHK flats
                  with identical headline prices can differ enormously in usable space, light and daily convenience.
                </p>
                <p>
                  This guide helps you compare options methodically — from carpet area and ventilation to RERA
                  verification — and introduces one verified option near Action Area III.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="twobhk-why-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="02" eyebrow="The Factors" title="Why Consider a 2 BHK in New Town?" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
              {WHY_2BHK.map((f, i) => (
                <Reveal key={f.title} delay={0.04 * i}>
                  <div data-testid={`twobhk-factor-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl p-6 h-full hover:border-[#84CC16] hover:-translate-y-1 hover:shadow-[0_20px_40px_-18px_rgba(77,124,15,0.18)] transition-all duration-500">
                    <div className="w-11 h-11 rounded-full bg-[#EAF5D6] border border-[#84CC16]/30 flex items-center justify-center text-[#365314]">
                      <f.icon size={19} strokeWidth={1.6} />
                    </div>
                    <h3 className="font-serif-display text-xl text-[#365314] mt-4 leading-snug">{f.title}</h3>
                    <p className="text-sm text-[#65A30D] mt-2 leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="twobhk-checklist-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="03" eyebrow="Due Diligence" title="What to Check Before Buying a 2 BHK" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  <strong className="text-[#365314]">Carpet area versus other measurements:</strong> carpet area is
                  the usable floor space inside the walls — always compare homes on carpet area, not just BUA or SBUA.
                  Also check the <strong className="text-[#365314]">floor plan</strong> for wasted passages, the{" "}
                  <strong className="text-[#365314]">balcony</strong> provision, and which{" "}
                  <strong className="text-[#365314]">tower and floor</strong> the unit sits on.
                </p>
                <p>
                  Assess <strong className="text-[#365314]">ventilation</strong> and open views in person where
                  possible, review the <strong className="text-[#365314]">amenities</strong> you will actually use,
                  ask for full pricing including{" "}
                  <strong className="text-[#365314]">applicable additional charges</strong>, confirm{" "}
                  <strong className="text-[#365314]">current inventory</strong> for your preferred tower, and verify{" "}
                  <strong className="text-[#365314]">RERA information</strong> for the project and the agent.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="twobhk-project-section" className="py-16 sm:py-24 bg-[#EAF5D6]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="04" eyebrow="One Verified Option" title="2 BHK at Vinayak 21 Acres" dark />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed max-w-3xl">
                For buyers considering 2 BHK flats near Action Area III Kolkata, Vinayak 21 Acres is one residential
                option to evaluate. The project is located at Hatishala — off and adjacent to Newtown Action Area III
                — and offers 2 BHK 2T residences within a development that features 90% homes with 3-side open views,
                double-height sky terraces, Vastu-compliant layouts, an IGBC Precertified Platinum green rating and a
                3-acre central park.
              </p>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
              {TWO_BHK_PLANS.map((p, i) => (
                <Reveal key={p.slug} delay={0.04 * (i % 4)}>
                  <div data-testid={`twobhk-plan-card-${p.slug}`}
                    className="border border-[#3F6212]/12 rounded-xl p-5 bg-[#3F6212]/[0.04] h-full flex flex-col">
                    <p className="font-mono-spec text-[10px] tracking-[0.2em] uppercase text-[#84CC16]">{p.tower}</p>
                    <h3 className="font-serif-display text-lg text-[#3F6212] mt-2">{p.config}</h3>
                    <p className="font-mono-spec text-[12px] text-[#4D7C0F] mt-3">Carpet {p.carpet} · SBUA {p.sbua}</p>
                    <button data-testid={`twobhk-view-${p.slug}`} onClick={() => setPlanView(p)}
                      className="mt-4 text-[12px] font-sub font-semibold border border-[#84CC16]/60 text-[#84CC16] rounded-full px-4 py-2.5 hover:bg-[#84CC16]/10 active:scale-[0.98] transition-all duration-300">
                      View Floor Plan
                    </button>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="twobhk-floorplan-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="05" eyebrow="Layout" title="Vinayak 21 Acres 2 BHK Floor Plan" />
            <Reveal delay={0.15}>
              <div className="bg-white border border-[#DCECC0] rounded-2xl p-7 sm:p-10 mt-8 shadow-[0_24px_48px_-24px_rgba(77,124,15,0.18)]">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                  {[["Carpet Area", "595–597 sq ft"], ["Balcony Area", "60 sq ft"], ["BUA", "728 sq ft"], ["SBUA", "971 sq ft"]].map(([label, value]) => (
                    <div key={label} className="border border-[#DCECC0] rounded-xl p-4">
                      <p className="text-[11px] text-[#65A30D] uppercase tracking-wider">{label}</p>
                      <p className="font-mono-spec text-sm text-[#365314] mt-1.5">{value}</p>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-[#4D7C0F] mt-6 leading-relaxed">
                  The 2 BHK 2T comes as Type B and Type G across Towers 1A, 1B, 2A and 2B, with carpet area varying
                  slightly by tower and type. View the official drawings above or browse all layouts on the floor
                  plans page.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button data-testid="twobhk-view-plan-btn" onClick={() => setPlanView(TWO_BHK_PLANS[0])}
                    className="bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-3.5 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    View Floor Plan
                  </button>
                  <Link to="/vinayak-21-acres-floor-plan" data-testid="twobhk-floorplan-link"
                    className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                    Vinayak 21 Acres Floor Plans <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="twobhk-price-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="06" eyebrow="Pricing" title="Vinayak 21 Acres 2 BHK Price" />
            <Reveal delay={0.15}>
              <div className="bg-white border border-[#DCECC0] rounded-2xl p-7 sm:p-10 mt-8">
                <p className="text-[#4D7C0F] text-base sm:text-lg leading-relaxed" data-testid="twobhk-price-note">
                  Pricing and availability are subject to change. Contact Forest Hill Realtors for the latest
                  developer-provided price and available inventory.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button data-testid="twobhk-get-price-btn" onClick={() => openModal("Get Latest 2 BHK Price")}
                    className="bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-3.5 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    Get Latest 2 BHK Price
                  </button>
                  <button data-testid="twobhk-check-availability-btn" onClick={() => openModal("Check 2 BHK Availability")}
                    className="text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                    Check 2 BHK Availability
                  </button>
                  <Link to="/vinayak-21-acres-price" data-testid="twobhk-price-link"
                    className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                    Vinayak 21 Acres Price <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="twobhk-location-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="07" eyebrow="Connectivity" title="Location & Connectivity" />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                From the Hatishala vicinity, indicative travel times include the nearest metro and Biswa Bangla Gate
                at about 12 minutes, the airport around 30 minutes, Infosys about 1 minute, Wipro and ITC Infotech
                about 2 minutes, Spencer's about 6 minutes, and DPS Newtown about 15 minutes. Travel times are
                indicative and may vary depending on traffic and route.
              </p>
            </Reveal>
          </div>
        </section>

        <section data-testid="twobhk-amenities-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="08" eyebrow="Lifestyle" title="Amenities & Lifestyle" />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                Life at the project centres on a 3-acre central park with elevated cabanas, a grand lawn, kids' play
                areas, an open-air amphitheatre, cascading waters and a maze garden, alongside sports options
                including futsal, lawn tennis, cricket, pickleball, badminton and basketball, a pet park, forest
                trail, jogging and cycling tracks, and a planned shuttle service to the nearest metro.
              </p>
            </Reveal>
          </div>
        </section>

        <section data-testid="twobhk-who-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="09" eyebrow="Fit" title="Who Might Consider a 2 BHK?" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  A 2 BHK often suits first-time buyers stepping into ownership, young couples planning ahead, small
                  families who want a dedicated second room, and professionals who prioritise a short commute to the
                  Action Area III employment hub. Buyers working to a defined budget may also find an efficient 2 BHK
                  a comfortable fit without over-stretching.
                </p>
                <p>
                  The right choice depends entirely on your household's needs and finances — compare configurations
                  carefully and verify all details before deciding.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="twobhk-visit-section" className="pb-16 sm:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl bg-[#EAF5D6] p-8 sm:p-12 text-center hero-grain">
                <p className="overline-tag">10 · Visit</p>
                <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3F6212] leading-tight mt-4">
                  Book a Site Visit
                </h2>
                <p className="text-[#4D7C0F] text-sm sm:text-base mt-4 max-w-xl mx-auto leading-relaxed">
                  Walk through a 2 BHK in person — feel the light, the ventilation and the neighbourhood before you
                  decide.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center max-w-2xl mx-auto">
                  <button data-testid="twobhk-book-visit-btn" onClick={() => openModal("Site Visit Booking")}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    <CalendarCheck size={16} /> Book a Site Visit
                  </button>
                  <a data-testid="twobhk-whatsapp-btn" href={waLink} target="_blank" rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-sub font-bold text-sm px-7 py-4 rounded-full hover:bg-[#1EBE57] active:scale-[0.98] transition-all duration-300">
                    <MessageSquare size={16} /> WhatsApp Us
                  </a>
                </div>
                <div className="mt-6">
                  <Link to="/best-property-in-new-town-kolkata" data-testid="twobhk-guide-link"
                    className="inline-flex items-center gap-2 text-[#84CC16] text-sm font-sub font-medium hover:underline">
                    Best Property in New Town Kolkata Guide <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="twobhk-faq-section" className="pb-16 sm:pb-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="11" eyebrow="FAQs" title="2 BHK Buyer FAQs" center />
            <Reveal delay={0.15}>
              <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="twobhk-faq-accordion">
                {TWO_BHK_FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={f.q} data-testid={`twobhk-faq-item-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl px-6 data-[state=open]:border-[#84CC16] transition-colors duration-300">
                    <AccordionTrigger data-testid={`twobhk-faq-trigger-${i}`}
                      className="text-left font-sub font-medium text-[15px] text-[#365314] hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-sm text-[#4D7C0F] leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>

        <div className="bg-[#EAF5D6] border-t border-[#DCECC0] py-7" data-testid="twobhk-partner-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <ShieldCheck size={17} className="text-[#84CC16] shrink-0" />
            <p className="text-[#4D7C0F] text-xs sm:text-[13px] leading-relaxed">
              <span className="text-[#3F6212] font-sub font-semibold">Forest Hill Realtors</span> · Authorised Channel
              Partner · WBRERA Agent Registration: <span className="font-mono-spec">{AGENT_REG}</span>
            </p>
          </div>
        </div>

        <Footer />
      </main>

      <AnimatePresence>
        {planView && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#F4FAE8]/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-8"
            onClick={() => setPlanView(null)} data-testid="twobhk-plan-viewer-overlay">
            <motion.div initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[92svh] overflow-y-auto p-4 sm:p-6"
              data-testid="twobhk-plan-viewer-modal">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="overline-tag">{planView.tower}</p>
                  <h3 className="font-serif-display text-2xl text-[#365314] mt-2">{planView.config}</h3>
                </div>
                <button data-testid="twobhk-plan-viewer-close-btn" onClick={() => setPlanView(null)}
                  className="p-2 text-[#65A30D] hover:text-[#365314] transition-colors" aria-label="Close">
                  <X size={22} />
                </button>
              </div>
              <img src={planView.planImage} alt={`Official floor plan — ${planView.config}, ${planView.tower}, Vinayak 21 Acres`}
                className="w-full h-auto rounded-lg border border-[#DCECC0]" />
              <p className="text-[11px] text-[#65A30D] mt-3 leading-relaxed">
                {planView.note} Plan from the official project brochure; subject to change — verify with Forest Hill Realtors.
              </p>
              <button data-testid="twobhk-plan-viewer-enquire-btn"
                onClick={() => { const s = `Floor Plan Enquiry: ${planView.config} · ${planView.tower}`; setPlanView(null); openModal(s); }}
                className="mt-5 w-full bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-6 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                Enquire About This Plan
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <LeadModal open={modal.open} onClose={() => setModal((m) => ({ ...m, open: false }))} source={modal.source} defaultInterest={modal.interest} whatsapp={contact.whatsapp} />
      <StickyMobileBar phone={contact.phone} whatsapp={contact.whatsapp} onEnquire={() => openModal("Quick Enquiry")} />
      <WhatsAppFloat whatsapp={contact.whatsapp} />
    </div>
  );
}
